import type { Session } from '@/types/auth';
import { isAxiosError, AxiosError, isCancel } from 'axios';

/**
 * Retrieve the value of a cookie by its name.
 *
 * @param name - The name of the cookie to retrieve.
 * @returns The value of the cookie if it exists; otherwise, an empty string.
 */
export function getCookie(name: string): string {
  return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
}

/**
 * Determines whether a given error indicates that the application is offline or has encountered a network-level
 * failure.
 *
 * This helper consolidates several heuristics:
 * - `navigator.onLine` is `false` (the browser reports no network connection).
 * - Axios network errors without a response (excluding canceled requests).
 * - Fetch-related TypeErrors that commonly signal lost connectivity.
 * - Dynamic import / chunk load failures (common when a network is disrupted).
 *
 * @param error - The error object thrown during rendering or data fetching.
 * @returns `true` if the error likely indicates an offline state or network-level failure, otherwise `false`.
 */
export function checkIfOffline(error: unknown): boolean {
  const errorString: string = toErrorMessage(error);

  // Detect offline / network-level failures:
  const isAxiosNetworkFailure: boolean = isAxiosError(error) && !error.response && !isCancel(error);

  // Some browsers throw TypeError("Failed to fetch") or “NetworkError when attempting to fetch resource.”
  const isFetchNetworkFailure: boolean = error instanceof TypeError &&
    /NetworkError|Failed to fetch/i.test(String(error.message));

  // Dynamic import / chunk load failures commonly show one of these phrases:
  const isChunkLoadFailure: boolean = /Failed to fetch dynamically imported module|Load failed|Loading chunk [\w-]+ failed|ChunkLoadError|Importing a module script failed/i.test(errorString);

  return (!navigator.onLine || isAxiosNetworkFailure || isFetchNetworkFailure || isChunkLoadFailure);
}

/**
 * Checks if a request URL is likely to return a large payload and may require an extended timeout.
 *
 * Slow endpoints include:
 *   - `/surveys/`
 *   - `/logins/`
 *   - `/progressions/`
 *
 * @param url - The Axios request URL.
 * @returns True if the URL matches a known slow endpoint.
 */
export const isPotentiallySlowRequest = (url: string): boolean  => (
  url.includes("/surveys/") ||
  url.includes("/logins/") ||
  url.includes("/progressions/")
);

/**
 * Verify that an object conforms to the `Session` interface shape.
 *
 * This function performs a runtime type guard check to ensure the object has the
 * required properties (`id`, `accessToken`, and `refreshToken`), all of which must be strings.
 *
 * @param obj - The object to test.
 * @returns `true` if the object matches the `Session` type; otherwise, `false`.
 */
function isSession(obj: any): obj is Session {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.id === 'string' &&
    typeof obj.accessToken === 'string' &&
    typeof obj.refreshToken === 'string'
  );
}

/**
 * Retrieve the session object from session storage and validate its shape.
 *
 * @returns A valid Session object if found and valid; otherwise, null.
 */
export function retrieveSessionFromStorage(): Session | null {
  const sessionJson: string | null = sessionStorage.getItem("session");
  if (!sessionJson) return null;

  try {
    const parsed = JSON.parse(sessionJson);
    if (isSession(parsed)) return parsed;
    console.warn("Invalid session object found in storage.");
  } catch (error) {
    console.error("Failed to parse session from storage:", error);
  }

  return null
}

/**
 * Type guard to check whether an error is an AxiosError caused by lack of authorization.
 *
 * Specifically, this checks for HTTP status codes:
 * - 401 (Unauthorized)
 * - 403 (Forbidden)
 *
 * @param error - The error object to inspect.
 * @returns `true` if the error is an AxiosError with status 401 or 403; otherwise `false`.
 */
export function isUnauthorized(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false;
  const status: number | undefined = error.response?.status;
  return status === 401 || status === 403;
}

/**
 * Convert an unknown error into a human-readable message.
 *
 * If the error is an AxiosError:
 *   - Prefer the `message` field from the response data (if available).
 *   - Otherwise, use `statusText`, then the error's own message.
 *
 * If the error is a generic Error, use its message. Otherwise, coerce the error to a string.
 *
 * @param error - The error to convert.
 * @returns A string message describing the error.
 */
export function toErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : String(error);
  }

  const message: any = (error.response?.data as any)?.message;
  if (typeof message === "string") {
    return message;
  }

  return error.response?.statusText ?? error.message ?? String(error);
}